import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { NetworkErrorDialog } from '../NetworkErrorDialog';
import { NetworkContext } from '../../contexts/NetworkContext';
import type { NetworkContextType } from '../../contexts/NetworkContext';
import { Wifi, CheckCircle } from 'lucide-react';

// Connection Restored Toast Component
const ConnectionRestoredToast: React.FC<{ isVisible: boolean; onHide: () => void }> = ({
  isVisible,
  onHide,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onHide, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onHide]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-[10000] animate-in slide-in-from-right duration-300">
      <div className="bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center space-x-3 border border-green-500">
        <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
          <CheckCircle className="w-5 h-5" />
        </div>
        <div>
          <p className="font-semibold">{t('network.reconnected') || 'Connection restored'}</p>
          <p className="text-green-100 text-sm">You're back online</p>
        </div>
        <Wifi className="w-5 h-5 text-green-200" />
      </div>
    </div>
  );
};

interface NetworkProviderProps {
  children: React.ReactNode;
}

const NetworkProvider: React.FC<NetworkProviderProps> = ({ children }) => {
  const { isOnline, isOffline, wasOffline } = useNetworkStatus();
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [hasShownOfflineDialog, setHasShownOfflineDialog] = useState(false);
  const [showConnectionRestored, setShowConnectionRestored] = useState(false);

  useEffect(() => {
    // Show network error dialog when going offline
    if (isOffline && !hasShownOfflineDialog) {
      setShowNetworkError(true);
      setHasShownOfflineDialog(true);
    }

    // Hide network error dialog and show connection restored toast when coming back online
    if (isOnline && wasOffline) {
      setShowNetworkError(false);
      setShowConnectionRestored(true);
      setHasShownOfflineDialog(false);

      // Auto-hide the connection restored toast after 3 seconds
      setTimeout(() => {
        setShowConnectionRestored(false);
      }, 3000);
    }
  }, [isOnline, isOffline, wasOffline, hasShownOfflineDialog]);

  const hideNetworkError = () => {
    setShowNetworkError(false);
  };

  const contextValue: NetworkContextType = {
    isOnline,
    isOffline,
    showNetworkError,
    hideNetworkError,
  };

  return (
    <NetworkContext.Provider value={contextValue}>
      {children}

      {/* Network Error Dialog */}
      <NetworkErrorDialog
        isOpen={showNetworkError}
        onClose={hideNetworkError}
        onRetry={() => {
          // Retry logic - reload page
          window.location.reload();
        }}
      />

      {/* Connection Restored Toast */}
      <ConnectionRestoredToast
        isVisible={showConnectionRestored}
        onHide={() => setShowConnectionRestored(false)}
      />
    </NetworkContext.Provider>
  );
};

export { NetworkProvider };
export default NetworkProvider;
